/**
 * Name dialog (#123): one text field for New folder and Rename. Built like
 * ConfirmDialog (#121): Bootstrap modal, shared dialog queue, all text via
 * textContent. The caller's submit decides: null closes the dialog, a text
 * is shown and keeps it open (e.g. a duplicate name).
 */

import { enqueueDialog } from './dialog-queue';
import { queuedModal } from './queued-modal';

export interface NameDialogOptions {
  title: string;
  label: string;
  initial: string;
  saveLabel: string;
  cancelLabel: string;
  submit: (name: string) => Promise<string | null>;
}

let instanceCounter = 0;

export function nameDialog(options: NameDialogOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    enqueueDialog((done) => {
      try {
        openDialog(options, resolve, done);
      } catch {
        resolve(false);
        done();
      }
    });
  });
}

function openDialog(
  { title, label, initial, saveLabel, cancelLabel, submit }: NameDialogOptions,
  resolve: (value: boolean) => void,
  done: () => void
): void {
  const id = `name-dialog-${++instanceCounter}`;

  const modal = document.createElement('div');
  modal.className = 'modal fade';
  modal.id = id;
  modal.tabIndex = -1;
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', `${id}-title`);

  const form = document.createElement('form');
  form.className = 'modal-content';
  form.dataset.nameForm = '';
  form.noValidate = true;

  const header = document.createElement('div');
  header.className = 'modal-header';
  const titleEl = document.createElement('h2');
  titleEl.className = 'modal-title fs-5';
  titleEl.id = `${id}-title`;
  titleEl.textContent = title;
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'btn-close';
  close.setAttribute('aria-label', cancelLabel);
  header.append(titleEl, close);

  const body = document.createElement('div');
  body.className = 'modal-body';
  const labelEl = document.createElement('label');
  labelEl.className = 'form-label';
  labelEl.htmlFor = `${id}-input`;
  labelEl.textContent = label;
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'form-control';
  input.id = `${id}-input`;
  input.maxLength = 255;
  input.value = initial;
  input.autocomplete = 'off';
  input.dataset.nameInput = '';
  input.setAttribute('aria-describedby', `${id}-error`);
  const error = document.createElement('div');
  error.className = 'invalid-feedback d-block';
  error.id = `${id}-error`;
  error.setAttribute('role', 'alert');
  error.dataset.nameError = '';
  body.append(labelEl, input, error);

  const footer = document.createElement('div');
  footer.className = 'modal-footer';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn btn-outline-secondary';
  cancel.dataset.nameAction = 'cancel';
  cancel.textContent = cancelLabel;
  const save = document.createElement('button');
  save.type = 'submit';
  save.className = 'btn btn-primary';
  save.dataset.nameAction = 'save';
  save.textContent = saveLabel;
  footer.append(cancel, save);

  form.append(header, body, footer);
  const dialog = document.createElement('div');
  dialog.className = 'modal-dialog modal-dialog-centered';
  dialog.appendChild(form);
  modal.appendChild(dialog);
  document.body.appendChild(modal);

  let settled = false;
  let busy = false;
  const settle = (value: boolean): void => {
    if (settled) return;
    settled = true;
    resolve(value);
  };
  const { hide, show } = queuedModal(modal, {
    done,
    onShown: () => {
      input.focus();
      input.select();
    },
    onClosed: () => settle(false),
  });
  const updateSave = (): void => {
    const name = input.value.trim();
    save.disabled = busy || name === '' || name === initial.trim();
  };
  const setError = (text: string): void => {
    error.textContent = text;
    input.classList.toggle('is-invalid', text !== '');
  };

  input.addEventListener('input', () => {
    setError('');
    updateSave();
  });
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (save.disabled) return;
    busy = true;
    cancel.disabled = true;
    close.disabled = true;
    updateSave();
    const name = input.value.trim();
    void Promise.resolve()
      .then(() => submit(name))
      .catch((e: unknown) => (e instanceof Error ? e.message : String(e)))
      .then((result) => {
        busy = false;
        cancel.disabled = false;
        close.disabled = false;
        if (result === null) {
          settle(true);
          hide();
          return;
        }
        setError(result);
        updateSave();
        input.focus();
      });
  });
  cancel.addEventListener('click', () => {
    settle(false);
    hide();
  });
  close.addEventListener('click', () => {
    settle(false);
    hide();
  });
  // While a submit is pending the dialog cannot be dismissed: the caller must
  // learn the real outcome (true) so it reloads.
  modal.addEventListener('hide.bs.modal', (event) => {
    if (busy) {
      event.preventDefault();
      return;
    }
    settle(false);
  });

  updateSave();
  show();
}
